import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { InteractionManager } from "react-native";
import { Spinner, Text, View } from "native-base";

import { DeanteData } from "../../../Universal/API/Context-API/AppProvider";
import { UnitProps } from "../../../HomeScreen/Components/Unit";
import { formUnits } from "../../../HomeScreen/HomeScreen";
import ListOfUnitsV3, {
  defaultContentContainerStyleV3,
} from "../../../HomeScreen/Components/V3";
import {
  CatalogTypeItem,
  FilterConfigItem,
  FilterKey,
  FiltersState,
} from "../../types";
import FilterSection from "./Components/FilterSection/FilterSection";
import FilterSlideEmptyState from "./Components/FilterSlideEmptyState";
import FilterSlideHeader from "./Components/FilterSlideHeader";
import { getCatalogTypeDisplayName } from "../../utils";

const Slide2Placeholder = React.memo(() => {
  return (
    <View
      style={{
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
      }}
    >
      <Spinner />
      <Text mt={3} color="coolGray.500">
        Загрузка…
      </Text>
    </View>
  );
});

type FilterSlideProps = {
  enabled: boolean;
  selectedType: CatalogTypeItem | null;
  onBackToTypes: () => void;
  navigation: any;
  allUnitsData: DeanteData[];
  filters: FiltersState;
  filterConfig: FilterConfigItem[];
  toggleFilterValue: (key: FilterKey, value: string) => void;
  clearFilters: () => void;
  isLeavingRef: RefObject<boolean>;
  isFocused?: boolean;
};

const FilterSlide: React.FC<FilterSlideProps> = React.memo(
  ({
    enabled,
    selectedType,
    allUnitsData,
    filters,
    filterConfig,
    toggleFilterValue,
    clearFilters,
    onBackToTypes,
    navigation,
    isLeavingRef,
    isFocused = true,
  }) => {
    const [listUnits, setUnits] = useState<UnitProps["unit"][]>([]);

    const baseUnits = useMemo<DeanteData[]>(
      () => (allUnitsData ? allUnitsData : []),
      [allUnitsData]
    );

    const filteredUnits = useMemo<DeanteData[]>(() => {
      let units = baseUnits;

      const selectedColors = filters.colors ?? [];
      if (selectedColors.length > 0) {
        const wanted = new Set(selectedColors.map((c) => c.toLowerCase()));
        units = units.filter((u) => {
          const colors = [(u as any).pal]
            .filter(Boolean)
            .map((c) => String(c).toLowerCase());
          return colors.some((c) => wanted.has(c));
        });
      }

      const selectedTypes = filters.types ?? [];
      if (selectedTypes.length > 0) {
        const wanted = new Set(selectedTypes.map((t) => t.toLowerCase()));
        units = units.filter((u) =>
          wanted.has(String((u as any).type ?? "").toLowerCase())
        );
      }

      const selectedPrims = filters.prims ?? [];
      if (selectedPrims.length > 0) {
        const wanted = new Set(selectedPrims.map((t) => t.toLowerCase()));
        units = units.filter((u) =>
          wanted.has(String((u as any).prim ?? "").toLowerCase())
        );
      }

      const selectedCollections = filters.collections ?? [];
      if (selectedCollections.length > 0) {
        const wanted = new Set(selectedCollections.map((t) => t.toLowerCase()));
        units = units.filter((u) =>
          wanted.has(String((u as any).col ?? "").toLowerCase())
        );
      }

      return units;
    }, [baseUnits, filters]);

    useEffect(() => {
      if (!enabled) return;
      if (isLeavingRef.current) return;

      let cancelled = false;

      const fallbackId = setTimeout(() => {
        if (cancelled || isLeavingRef.current) return;
        formUnits({ setUnits, result: filteredUnits ?? [] });
      }, 0);

      const task = InteractionManager.runAfterInteractions(() => {
        if (cancelled || isLeavingRef.current) return;
        clearTimeout(fallbackId);
        formUnits({ setUnits, result: filteredUnits ?? [] });
      });

      return () => {
        cancelled = true;
        clearTimeout(fallbackId);
        // @ts-ignore
        task?.cancel?.();
      };
    }, [enabled, filteredUnits, isLeavingRef]);

    const hasAnySelection =
      (filters.colors?.length ?? 0) > 0 ||
      (filters.types?.length ?? 0) > 0 ||
      (filters.prims?.length ?? 0) > 0 ||
      (filters.collections?.length ?? 0) > 0;

    const renderHeader = useCallback(() => {
      return (
        <FilterSlideHeader
          title={getCatalogTypeDisplayName(selectedType?.type)}
          canClear={hasAnySelection}
          resultCount={filteredUnits.length}
          onClear={clearFilters}
        >
          {filterConfig.map((filterItem) => {
            const typesDisabled =
              filterItem.key === "types" && (filters.prims?.length ?? 0) === 0;

            return (
              <FilterSection
                key={filterItem.key}
                title={filterItem.title}
                filterKey={filterItem.key}
                options={filterItem.options}
                unavailableOptions={filterItem.unavailableOptions}
                presentation={filterItem.presentation}
                inlineLabel={filterItem.inlineLabel}
                placeholder={filterItem.placeholder}
                clearLabel={filterItem.clearLabel}
                selectedValues={filters[filterItem.key] ?? []}
                onToggle={toggleFilterValue}
                disabled={typesDisabled}
              />
            );
          })}
        </FilterSlideHeader>
      );
    }, [
      clearFilters,
      filterConfig,
      filters,
      hasAnySelection,
      onBackToTypes,
      selectedType,
      toggleFilterValue,
    ]);

    if (!selectedType) {
      return <FilterSlideEmptyState onBackToTypes={onBackToTypes} />;
    }

    if (!enabled) {
      return <Slide2Placeholder />;
    }

    return (
      isFocused && (
        <ListOfUnitsV3
          renderHeader={renderHeader()}
          navigation={navigation}
          listOfUnis={listUnits}
          contentContainerStyle={defaultContentContainerStyleV3}
        />
      )
    );
  }
);

export default FilterSlide;
